import { useState } from 'preact/hooks';
import type { AccountSave } from '../../game/account';
import { type CharacterSave, itemLevel } from '../../game/character';
import type { Game } from '../../game/Game';
import { netUi } from '../../net/netStore';
import { genericIcon } from '../../ui/icons';
import { ui } from '../../ui/app/store';
import { Window } from '../../ui/app/Window';
import { Cell, TipLayer, Wallet, toast, useCtx } from '../economy/ui';
import { ACTIVITIES, type Activity, type ActivityEntry, type EntryStatus, entryLabel, entryStatus, newRunId } from './catalog';

let gameRef: Game | null = null;
export function setPanelGame(g: Game): void {
  gameRef = g;
}

/** Zone kinds you can set out from (not from inside another instance). */
const ENTER_FROM = new Set(['town', 'field']);

const hex = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;

function initialSelection(): { act: string; entry: number } {
  const arg = ui.panelArg.value;
  if (arg === 'arena') return { act: 'crucible', entry: 0 };
  if (arg && ACTIVITIES.some((a) => a.id === arg)) return { act: arg, entry: 0 };
  return { act: 'rift', entry: 0 };
}

interface PartyInfo {
  size: number;
  leader: boolean;
}

function partyInfo(): PartyInfo {
  const members = netUi.party.value;
  const online = members.filter((m) => m.peer);
  const me = members.find((m) => m.self);
  return { size: online.length, leader: !!me?.leader };
}

/** One-line status chip for the activity list. */
function listChip(act: Activity, c: CharacterSave, a: AccountSave): { text: string; tone: string } {
  const first = act.entries[0];
  if (!first) return { text: '', tone: 'grey' };
  const st = entryStatus(act, first, c, a);
  switch (act.id) {
    case 'story': {
      const n = act.entries.filter((e) => c.flags[`${e.key}_cleared`]).length;
      return { text: `${n}/${act.entries.length} cleared`, tone: n === act.entries.length ? 'green' : 'gold' };
    }
    case 'rift':
      return { text: st.lockout[0]?.text.replace('Rift Energy ', '⚡ ') ?? '', tone: st.lockout[0]?.tone ?? 'grey' };
    case 'colossus':
      return st.lockout[0]?.tone === 'grey' ? { text: 'Claimed', tone: 'grey' } : { text: 'Claim ready', tone: 'green' };
    case 'deepvault':
    case 'siege': {
      const any = act.entries.map((e) => entryStatus(act, e, c, a));
      if (any.every((s) => s.zone === null)) return { text: 'Weekly done', tone: 'grey' };
      const done = act.id === 'deepvault' ? c.weekly.deepvault.length : c.weekly.siege.length;
      const total = act.id === 'deepvault' ? 2 : 3;
      return { text: `${c.weekly.week === a.week ? done : 0}/${total} gates this week`, tone: 'blue' };
    }
    case 'crucible':
      return { text: 'Open', tone: 'green' };
  }
}

function Req({ ok, label }: { ok: boolean; label: string }) {
  return <span class={`inst-req ${ok ? 'ok' : 'bad'}`}>{label}</span>;
}

function EntryRow({ act, e, st, c, sel, onSel }: { act: Activity; e: ActivityEntry; st: EntryStatus; c: CharacterSave; sel: boolean; onSel: () => void }) {
  const ilvl = itemLevel(c);
  return (
    <button type="button" class={`inst-entry${sel ? ' is-sel' : ''}${st.canEnter ? '' : ' is-locked'}`} onClick={onSel} aria-pressed={sel}>
      <span class="inst-entry-main">
        <span class="inst-entry-name">{e.name}</span>
        <span class="inst-entry-sub">{e.sub}</span>
      </span>
      <span class="inst-entry-reqs">
        <Req ok={c.level >= e.minLevel} label={`Lv ${e.minLevel}`} />
        {e.ilvl > 0 && <Req ok={ilvl >= e.ilvl} label={`${e.ilvlRequired ? '' : '≈'}iLvl ${e.ilvl}`} />}
        {act.id === 'story' && c.flags[`${e.key}_cleared`] ? <span class="inst-tag green">Cleared</span> : null}
      </span>
    </button>
  );
}

/** Panel `instances`: every instanced activity, its requirements, lockouts and entry. */
export function InstancesPanel() {
  const ctx = useCtx();
  const [sel, setSel] = useState(initialSelection);
  if (!ctx) return null;
  const { c, a } = ctx;
  const game = gameRef;
  const act = ACTIVITIES.find((x) => x.id === sel.act) ?? ACTIVITIES[0];
  const entryIdx = Math.max(0, Math.min(sel.entry, (act?.entries.length ?? 1) - 1));
  const entry = act?.entries[entryIdx];
  if (!act || !entry) return null;
  const st = entryStatus(act, entry, c, a);
  const party = partyInfo();
  const online = !!game?.net?.online;
  const queued = netUi.queue.value;
  const here = game?.zone?.layout.kind ?? 'town';
  const canLeaveHere = ENTER_FROM.has(here);
  const partyMember = party.size > 1 && !party.leader;
  const blockedEnter = !canLeaveHere ? 'Leave your current instance first' : partyMember ? 'Your party leader starts group content' : st.blocked;
  const queueSpec = act.queue;
  const hardSiege = act.id === 'siege' && entry.opts.diff === 'hard';
  const queueBlock = !queueSpec
    ? null
    : !online
      ? 'Matchmaking needs online play'
      : party.size > 1
        ? 'Parties enter together with Enter'
        : hardSiege
          ? 'Hard mode needs a premade party'
          : !canLeaveHere
            ? 'Leave your current instance first'
            : (st.blocked ?? (queued ? 'Already queued' : null));

  const enter = () => {
    if (!game || !st.zone || blockedEnter) return;
    if (st.underGeared) toast(`Recommended item level ${entry.ilvl} — this will be a hard fight`, '#e5a24d');
    game.enterInstance(st.zone, { ...entry.opts, run: newRunId() });
  };
  const queue = () => {
    if (!game?.net || !queueSpec || !st.zone || queueBlock) return;
    game.net.joinQueue({ content: st.zone, label: entryLabel(act, entry, st.zone), size: queueSpec.size, min: queueSpec.min, pvp: queueSpec.pvp, opts: { ...entry.opts, run: newRunId() } });
  };
  const hue = hex(act.hue);

  return (
    <Window title="Instances" width="min(1000px, 96vw)" height="min(660px, 90vh)" className="fx-win inst-win">
      <div class="inst-layout">
        <nav class="inst-list fx-scroll" aria-label="Activities">
          {ACTIVITIES.map((x) => {
            const chip = listChip(x, c, a);
            const on = x.id === act.id;
            return (
              <button type="button" key={x.id} class={`inst-card${on ? ' is-sel' : ''}`} style={{ '--hue': hex(x.hue) }} onClick={() => setSel({ act: x.id, entry: 0 })} aria-pressed={on}>
                <img class="inst-card-icon" src={genericIcon(`inst_${x.id}`, x.hue, x.glyph)} alt="" draggable={false} />
                <span class="inst-card-text">
                  <span class="inst-card-name">{x.name}</span>
                  <span class="inst-card-tag">{x.tagline}</span>
                </span>
                <span class={`inst-chip ${chip.tone}`}>{chip.text}</span>
              </button>
            );
          })}
        </nav>

        <section class="inst-detail" style={{ '--hue': hue }}>
          <header class="inst-hero">
            <div class="inst-hero-text">
              <h2>{act.name}</h2>
              <p class="inst-hero-tag">{act.tagline}</p>
              <span class="inst-pill">{act.players}</span>
            </div>
            <img class="inst-hero-art" src={genericIcon(`inst_${act.id}`, act.hue, act.glyph)} alt="" draggable={false} />
          </header>

          <div class="inst-body fx-scroll">
            <p class="inst-desc">{act.description}</p>

            {act.entries.length > 1 && <div class="section-h">{act.id === 'siege' ? 'Difficulty' : act.id === 'colossus' ? 'Colossus' : 'Dungeon'}</div>}
            <div class="inst-entries">
              {act.entries.map((e, i) => (
                <EntryRow key={e.key} act={act} e={e} st={entryStatus(act, e, c, a)} c={c} sel={i === entryIdx} onSel={() => setSel({ act: act.id, entry: i })} />
              ))}
            </div>

            <div class="inst-cols">
              <div>
                <div class="section-h">Rules</div>
                <ul class="inst-rules">
                  {act.rules.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div class="section-h">Status</div>
                <ul class="inst-status">
                  {st.lockout.map((l) => (
                    <li key={l.text} class={`tone-${l.tone}`}>
                      {l.text}
                    </li>
                  ))}
                  <li class="tone-grey">
                    Your item level: <b>{itemLevel(c).toFixed(0)}</b>
                    {entry.ilvl > 0 ? ` / ${entry.ilvlRequired ? 'required' : 'recommended'} ${entry.ilvl}` : ''}
                  </li>
                </ul>
              </div>
            </div>

            <div class="section-h">Rewards</div>
            <div class="inst-rewards">
              {act.rewards.map((id) => (
                <Cell key={id} item={id} size={38} />
              ))}
            </div>
          </div>

          <footer class="inst-foot">
            <div class="inst-party">
              {party.size > 1 ? (
                party.leader ? (
                  <span>
                    <b>Party of {party.size}</b> — you lead: members get a ready check
                  </span>
                ) : (
                  <span>
                    <b>Party of {party.size}</b> — your leader starts group content
                  </span>
                )
              ) : (
                <span>Entering alone{queueSpec && online ? ' — or find a group' : ''}</span>
              )}
              {(blockedEnter ?? st.underGeared) && (
                <span class={`inst-warn${blockedEnter ? '' : ' soft'}`}>{blockedEnter ?? `Below the recommended item level (${entry.ilvl})`}</span>
              )}
            </div>
            <Wallet a={a} c={c} tokens={act.id === 'crucible' ? ['arena_crest'] : act.id === 'siege' ? ['warlord_relic'] : act.id === 'deepvault' ? ['vault_token'] : act.id === 'colossus' ? ['colossus_mark'] : act.id === 'rift' ? ['rift_sigil'] : []} />
            <div class="inst-buttons">
              {queueSpec && (
                <button type="button" class="btn" disabled={!!queueBlock} title={queueBlock ?? `Matchmaking: ${queueSpec.pvp ? `${queueSpec.size / 2}v${queueSpec.size / 2}` : `up to ${queueSpec.size}`}`} onClick={queue}>
                  Matchmaking ({queueSpec.pvp ? `${queueSpec.size / 2}v${queueSpec.size / 2}` : queueSpec.size})
                </button>
              )}
              <button type="button" class="btn btn--gold inst-enter" disabled={!!blockedEnter || !st.zone} onClick={enter}>
                {party.size > 1 && party.leader ? 'Enter with party' : 'Enter'}
              </button>
            </div>
          </footer>
        </section>
      </div>
      <TipLayer />
    </Window>
  );
}
